import React from "react";
import { flexRender, type Table as TanstackTable } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/data-table";
import { useTranslations } from "next-intl";

type PinConfig = {
  left?: string[];
  right?: string[];
};

interface DataTableAggregateProps<TData>
  extends React.HTMLAttributes<HTMLDivElement> {
  table: TanstackTable<TData>;
  floatingBar?: React.ReactNode | null;
  pin?: PinConfig;
  footerContent?: React.ReactNode;
}

type ColumnAlign = "left" | "center" | "right";

const getColumnAlignClassName = (align?: ColumnAlign) => {
  switch (align) {
    case "right":
      return "text-right";
    case "center":
      return "text-center";
    default:
      return "";
  }
};

export default function DataTableAggregate<TData>({
  table,
  floatingBar = null,
  pin,
  footerContent,
  className,
  ...props
}: DataTableAggregateProps<TData>) {
  const t = useTranslations();
  const pinnedLeft = new Set(pin?.left ?? []);
  const pinnedRight = new Set(pin?.right ?? []);
  const isColumnResizingEnabled = Boolean(table.options.enableColumnResizing);
  const actionColumnWidth = 56;
  const rowNumberColumnWidth = 48;
  const headerRefs = React.useRef<Record<string, HTMLTableCellElement | null>>(
    {}
  );
  const hasManualColumnSizing =
    isColumnResizingEnabled &&
    Object.keys(table.getState().columnSizing ?? {}).length > 0;
  const [hasInitializedColumnSizing, setHasInitializedColumnSizing] =
    React.useState(!isColumnResizingEnabled);
  const serverPaginationMeta =
    (
      table.options.meta as
        | {
            currentPage?: number;
            currentPageSize?: number;
          }
        | undefined
    ) ?? {};
  const displayPageSize =
    serverPaginationMeta.currentPageSize ??
    table.getState().pagination.pageSize ??
    10;
  const displayPage =
    serverPaginationMeta.currentPage ??
    (table.getState().pagination.pageIndex ?? 0) + 1;
  const rowNumberStart =
    (displayPage - 1) * displayPageSize;
  const measureColumnSizing = React.useCallback(() => {
    return table.getFlatHeaders().reduce<Record<string, number>>(
      (acc, header) => {
        const element = headerRefs.current[header.id];

        if (!element || header.isPlaceholder) {
          return acc;
        }

        const width = Math.round(element.getBoundingClientRect().width);

        if (width > 0) {
          acc[header.column.id] = width;
        }

        return acc;
      },
      {}
    );
  }, [table]);

  const ensureColumnSizingInitialized = React.useCallback(() => {
    if (!isColumnResizingEnabled || hasManualColumnSizing) {
      return;
    }

    const measuredColumnSizing = measureColumnSizing();

    if (Object.keys(measuredColumnSizing).length > 0) {
      table.setColumnSizing(measuredColumnSizing);
      setHasInitializedColumnSizing(true);
    }
  }, [
    hasManualColumnSizing,
    isColumnResizingEnabled,
    measureColumnSizing,
    table,
  ]);

  React.useLayoutEffect(() => {
    if (!isColumnResizingEnabled) {
      setHasInitializedColumnSizing(true);
      return;
    }

    if (hasManualColumnSizing) {
      setHasInitializedColumnSizing(true);
      return;
    }

    const columnSizing = measureColumnSizing();

    if (Object.keys(columnSizing).length > 0) {
      table.setColumnSizing(columnSizing);
    }

    setHasInitializedColumnSizing(true);
  }, [
    hasManualColumnSizing,
    isColumnResizingEnabled,
    measureColumnSizing,
    table,
  ]);

  return (
    <div
      className={cn("w-full space-y-3", className)}
      {...props}
    >
      <div className="relative isolate max-h-[40rem] overflow-auto rounded-xl border border-slate-200/80 bg-white shadow-surface dark:border-slate-700/80 dark:bg-slate-800 dark:shadow-none">
        <div className={cn(hasManualColumnSizing && "min-w-full")}>
          <Table
            className={cn(hasManualColumnSizing ? "w-max" : "min-w-full")}
            style={
              hasManualColumnSizing
                ? {
                    width: table.getTotalSize(),
                  }
                : undefined
            }
          >
          <TableHeader className="sticky top-0 z-[60] border-b border-slate-200 bg-slate-100/90 backdrop-blur supports-[backdrop-filter]:bg-slate-100/80 dark:border-slate-700 dark:bg-slate-900/90 dark:supports-[backdrop-filter]:bg-slate-900/80">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                <TableHead
                  className="h-10 border-r border-slate-200/50 px-3 py-2 text-xs font-semibold text-slate-600 dark:border-slate-700/50 dark:text-slate-400"
                  style={{
                    width: rowNumberColumnWidth,
                    minWidth: rowNumberColumnWidth,
                    maxWidth: rowNumberColumnWidth,
                  }}
                >
                  {t("feature.common.row-number")}
                </TableHead>
                {headerGroup.headers.map((header) => {
                  const colId = header.column.id;
                  const isPinnedLeft = pinnedLeft.has(colId);
                  const isPinnedRight = pinnedRight.has(colId);
                  const isActionColumn = colId === "actions";
                  const columnAlign = (
                    header.column.columnDef.meta as
                      | {
                          align?: ColumnAlign;
                        }
                      | undefined
                  )?.align;
                  const isResizableColumn =
                    isColumnResizingEnabled &&
                    hasInitializedColumnSizing &&
                    header.column.getCanResize() &&
                    !isActionColumn;
                  const resizeHandler = header.getResizeHandler();

                  return (
                    <TableHead
                      key={header.id}
                      ref={(element) => {
                        headerRefs.current[header.id] = element;
                      }}
                      className={cn(
                        "relative h-10 border-r border-slate-200/50 px-3 py-2 text-xs font-semibold text-slate-600 dark:border-slate-700/50 dark:text-slate-400",
                        getColumnAlignClassName(columnAlign),
                        isActionColumn && "w-14 px-2",
                        isPinnedLeft &&
                          "sticky left-0 z-[70] bg-slate-100/90 backdrop-blur dark:bg-slate-900/90",
                        isPinnedRight &&
                          "sticky right-0 z-[70] bg-slate-100/90 backdrop-blur dark:bg-slate-900/90"
                      )}
                      style={
                        isActionColumn
                          ? {
                              width: actionColumnWidth,
                              minWidth: actionColumnWidth,
                              maxWidth: actionColumnWidth,
                            }
                          : hasManualColumnSizing
                          ? {
                              width: header.getSize(),
                              minWidth: header.getSize(),
                              maxWidth: header.getSize(),
                            }
                          : undefined
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {isResizableColumn && (
                        <div
                          onDoubleClick={() => header.column.resetSize()}
                          onMouseDown={(event) => {
                            ensureColumnSizingInitialized();
                            resizeHandler(event);
                          }}
                          onTouchStart={(event) => {
                            ensureColumnSizingInitialized();
                            resizeHandler(event);
                          }}
                          className={cn(
                            "absolute -right-[5px] top-0 z-10 h-full w-2.5 cursor-col-resize select-none touch-none bg-transparent transition-colors after:pointer-events-none after:absolute after:left-1/2 after:top-1 after:h-[calc(100%-0.5rem)] after:w-px after:-translate-x-1/2 after:bg-slate-300/80 hover:after:bg-primaryAlt/70",
                            header.column.getIsResizing() &&
                              "after:w-[2px] after:bg-primaryAlt"
                          )}
                        />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody className="bg-white dark:bg-slate-800">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="group border-b border-slate-200/40 transition-colors hover:bg-primaryAlt/[0.06] data-[state=selected]:bg-primaryAlt/[0.1] dark:border-slate-700/50 dark:hover:bg-primaryAlt/[0.16] dark:data-[state=selected]:bg-primaryAlt/[0.24]"
                >
                  <TableCell
                    className="border-r border-slate-200/50 px-3 py-2.5 align-middle text-[13px] text-slate-700 dark:border-slate-700/50 dark:text-slate-300"
                    style={{
                      width: rowNumberColumnWidth,
                      minWidth: rowNumberColumnWidth,
                      maxWidth: rowNumberColumnWidth,
                    }}
                  >
                    {rowNumberStart + row.index + 1}
                  </TableCell>
                  {row.getVisibleCells().map((cell) => {
                    const colId = cell.column.id;
                    const isPinnedLeft = pinnedLeft.has(colId);
                    const isPinnedRight = pinnedRight.has(colId);
                    const isActionColumn = colId === "actions";
                    const columnAlign = (
                      cell.column.columnDef.meta as
                        | {
                            align?: ColumnAlign;
                          }
                        | undefined
                    )?.align;

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "relative min-w-16 max-w-xs truncate border-r border-slate-200/40 px-3 py-2.5 align-middle text-[13px] text-slate-700 dark:border-slate-700/40 dark:text-slate-300",
                          getColumnAlignClassName(columnAlign),
                          isActionColumn && "w-14 px-2",
                          isPinnedLeft &&
                            "sticky left-0 z-[20] border-slate-200/50 bg-white group-hover:bg-primaryAlt/[0.06] dark:border-slate-700/50 dark:bg-slate-800 dark:group-hover:bg-primaryAlt/[0.16]",
                          isPinnedRight &&
                            "sticky right-0 z-[20] border-slate-200/50 bg-white group-hover:bg-primaryAlt/[0.06] dark:border-slate-700/50 dark:bg-slate-800 dark:group-hover:bg-primaryAlt/[0.16]"
                        )}
                        style={
                          isActionColumn
                            ? {
                                width: actionColumnWidth,
                                minWidth: actionColumnWidth,
                                maxWidth: actionColumnWidth,
                              }
                            : hasManualColumnSizing
                            ? {
                                width: cell.column.getSize(),
                                minWidth: cell.column.getSize(),
                                maxWidth: cell.column.getSize(),
                              }
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length + 1}
                  className="h-24 text-center text-muted-foreground"
                >
                  {t("feature.common.no-results")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          <TableFooter className="sticky bottom-0 z-[60] border-t border-slate-200 bg-slate-100/90 backdrop-blur supports-[backdrop-filter]:bg-slate-100/80 dark:border-slate-700 dark:bg-slate-900/90 dark:supports-[backdrop-filter]:bg-slate-900/80">
            {table.getFooterGroups().map((footerGroup) => (
              <TableRow key={footerGroup.id}>
                <TableHead
                  className="h-10 border-r border-slate-200/50 px-3 py-2 text-xs font-semibold text-slate-700 dark:border-slate-700/50 dark:text-slate-300"
                  style={{
                    width: rowNumberColumnWidth,
                    minWidth: rowNumberColumnWidth,
                    maxWidth: rowNumberColumnWidth,
                  }}
                >
                  &nbsp;
                </TableHead>
                {footerGroup.headers.map((header) => {
                  const colId = header.column.id;
                  const isPinnedLeft = pinnedLeft.has(colId);
                  const isPinnedRight = pinnedRight.has(colId);
                  const isActionColumn = colId === "actions";
                  const columnAlign = (
                    header.column.columnDef.meta as
                      | {
                          align?: ColumnAlign;
                        }
                      | undefined
                  )?.align;

                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "relative h-10 border-r border-slate-200/50 px-3 py-2 text-xs font-semibold text-slate-700 dark:border-slate-700/50 dark:text-slate-300",
                        getColumnAlignClassName(columnAlign),
                        isActionColumn && "w-14 px-2",
                        isPinnedLeft &&
                          "sticky left-0 z-[70] bg-slate-100/90 backdrop-blur dark:bg-slate-900/90",
                        isPinnedRight &&
                          "sticky right-0 z-[70] bg-slate-100/90 backdrop-blur dark:bg-slate-900/90"
                      )}
                      style={
                        isActionColumn
                          ? {
                              width: actionColumnWidth,
                              minWidth: actionColumnWidth,
                              maxWidth: actionColumnWidth,
                            }
                          : hasManualColumnSizing
                          ? {
                              width: header.getSize(),
                              minWidth: header.getSize(),
                              maxWidth: header.getSize(),
                            }
                          : undefined
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableFooter>
          </Table>
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <DataTablePagination table={table} leftContent={footerContent} />
        {table.getFilteredSelectedRowModel().rows.length > 0 && floatingBar}
      </div>
    </div>
  );
}
